'use client'
import { useMemo, useState } from 'react'
import Link from 'next/link'
import styles from './CustomNav.module.css'

const removeString = (data) =>
  data && data.length > 20 ? data.slice(0, 11) + '...' : data

export default function CustomNav({
  cusomNav = [],
  headerStyle,
  activeCustomMenuId, // ✅ 外層控制整體是否啟用亮起
  onActivate,
}) {
  // 攤平成平面資料
  const flatList = useMemo(() => {
    const res = []
    const walk = (arr) => {
      arr.forEach((n) => {
        res.push(n)
        if (n.Children?.length) walk(n.Children)
      })
    }
    walk(cusomNav)
    return res
  }, [cusomNav])

  const parents = useMemo(
    () => flatList.filter((x) => x.ParentID === null && x.Active !== false),
    [flatList]
  )

  const getChildren = (parentId) =>
    flatList.filter((x) => x.ParentID === parentId)

  const resolveHref = (node) => {
    // console.log(node)
    if (!node?.IsInternalURL) {
      return node?.ExternalURL?.trim() || '#'
    }

    if (!node?.LinkURL) return '#'

    const link = node.LinkURL.trim()
    const para = node.LinkURLPagePara?.trim() ?? ''

    // 首頁
    if (link === '/') return '/'

    // 判斷是否為「詳情頁」
    const isDetail =
      link.includes('Detail') ||
      link.includes('DetailPage') ||
      link.includes('Detail/') ||
      link.startsWith('/product/') // 商品詳情

    // 判斷是否為「列表頁」
    const isList =
      link.includes('List') ||
      link.includes('list') ||
      link.startsWith('/product-list')

    const isProductListCategory = link.startsWith('/product-list?categoryid=')
    const isProductListBrand = link.startsWith('/product-list?brandid=')
    const isMemberCenter = link.startsWith('/MemberCenter')
    const isBlogList = link.startsWith('/blogs-list')
    const isActivity = link.startsWith('/activity')

    let finalLink = ''

    if (isDetail) {
      // 詳情頁要加參數
      finalLink = link.endsWith('/') ? `${link}${para}` : `/${link}${para}`
    } else if (isList) {
      // 列表頁不加斜線、不加參數（除了 product-list 特例）
      if (isProductListCategory || isProductListBrand) {
        finalLink = `${link}${para}` // 保留參數
      } else if (isBlogList) {
        finalLink = `${link}/${para}`
      } else {
        finalLink = link // 不加任何參數
      }
    } else if (isActivity) {
      finalLink = `${link}/${para}`
    } else if (isMemberCenter) {
      // 會員中心保持原樣
      finalLink = `${link}${para}`
    } else {
      // 其他一般頁面要加斜線，但不帶參數
      finalLink = link.startsWith('/') ? `${link}` : `/${link}`
    }

    // 防呆：移除多餘斜線
    finalLink = finalLink.replace(/\/{2,}/g, '/')

    return finalLink
  }

  return (
    <>
      {parents.map((parent, idx) => (
        <ParentNavItem
          key={`parent-${parent.MenuID}-${idx}`}
          parent={parent}
          getChildren={getChildren}
          resolveHref={resolveHref}
          headerStyle={headerStyle}
          styles={styles}
          activeCustomMenuId={activeCustomMenuId}
          onActivate={onActivate}
        />
      ))}
    </>
  )
}

function ParentNavItem({
  parent,
  getChildren,
  resolveHref,
  headerStyle,
  styles,
  activeCustomMenuId,
  onActivate,
}) {
  const [menuVisible, setMenuVisible] = useState(false)
  const [hoverPath, setHoverPath] = useState([])

  // 🟢 產生多層結構
  const menuLayers = useMemo(() => {
    const layers = []
    let currentParentId = parent.MenuID
    for (let i = 0; i <= hoverPath.length; i++) {
      const items = getChildren(currentParentId)
      if (items.length > 0) layers.push(items)
      currentParentId = hoverPath[i]
    }
    return layers
  }, [parent.MenuID, hoverPath, getChildren])

  const handleHover = (levelIndex, node) => {
    const next = hoverPath.slice(0, levelIndex)
    next[levelIndex] = node.MenuID
    setHoverPath(next)
  }

  const handleMouseLeave = () => {
    setHoverPath([])
    setMenuVisible(false)
  }

  return (
    <li
      className="navigation__item"
      style={{ position: 'relative' }}
      onMouseEnter={() => setMenuVisible(true)}
      onMouseLeave={handleMouseLeave}
    >
      {/* 父層 */}
      {resolveHref(parent) !== '' ? (
        <Link
          href={resolveHref(parent)}
          className={`navigation__link ${styles.parentNavLink}`}
          onClick={() => {
            setMenuVisible(false)
            onActivate?.(parent.MenuID)
          }}
          style={{
            color:
              activeCustomMenuId === parent.MenuID
                ? headerStyle?.HeaderFontFocusColor
                : headerStyle?.HeaderFontColor,
          }}
        >
          <span className={styles.parentNavText}>{parent.MenuName}</span>
        </Link>
      ) : (
        <span
          className={`navigation__link ${styles.parentNavLink}`}
          style={{
            cursor: 'default',
            color:
              activeCustomMenuId === parent.MenuID
                ? headerStyle?.HeaderFontFocusColor
                : headerStyle?.HeaderFontColor,
          }}
        >
          <span className={styles.parentNavText}>{parent.MenuName}</span>
        </span>
      )}

      {/* 下拉選單 */}
      {menuVisible && (
        <div className={styles.menuContainer}>
          {menuLayers.map((items, levelIndex) => (
            <ul key={levelIndex} className={styles.menuList}>
              {items.map((item, i) => {
                const children = getChildren(item.MenuID)
                const hasChildren = children.length > 0
                const itemHref = resolveHref(item)

                return (
                  <li
                    key={`${item.MenuID}-${levelIndex}-${i}`}
                    className={`${styles.menuItem}`}
                    onMouseEnter={() => handleHover(levelIndex, item)}
                  >
                    {hasChildren ? (
                      <span
                        className={`${styles.menuLink} ${styles.hasChildren}`}
                        style={{ cursor: 'default' }}
                      >
                        {removeString(item.MenuName)}
                        <span className={styles.chevron}>›</span>
                      </span>
                    ) : (
                      <Link
                        href={itemHref}
                        className={`${styles.menuLink} ${styles.noChildren}`}
                        onClick={() => {
                          setMenuVisible(false)
                          onActivate?.(parent.MenuID)
                        }}
                      >
                        {removeString(item.MenuName)}
                      </Link>
                    )}
                  </li>
                )
              })}
            </ul>
          ))}
        </div>
      )}
    </li>
  )
}
